import { getOperationName } from '@apollo/client/utilities';
import { UUID } from '@cmg/common';
import { SnackbarManager } from '@cmg/design-system';
import React from 'react';

import { InterestLevelInput } from '../../../../../../../../../graphql';
import { pendingOrderBookGridForcedRefreshVar } from '../../../../../../../../order-book';
import { updateDemandGridRowsReactiveVar } from '../../../../../../../../order-book/institutional-demand/hooks/useUpdateDemandGridRowsReactiveVar';
import { SyndicateIndicationQuery } from '../../../../../context/hooks/syndicate/__generated__';
import { IndicationForm_IndicationAcknowledgementsDocument } from '../../../../indication-form/components/indication-details/components/acknowledgement/graphql/__generated__';
import { IndicationFormValues } from '../../../common/utils/IndicationSectionValidation';
import { useManageIndication_UpdateIndicationMutation } from '../../../graphql/__generated__';

export const refetchQueries = [
  getOperationName(IndicationForm_IndicationAcknowledgementsDocument)!,
];

export const useUpdateInstitutionalIndication = (
  offeringId: UUID,
  indication: SyndicateIndicationQuery['basicPublishedOffering']['orderBook']['institutionalIndication'],
  onFinishEditing: () => void
) => {
  const [updateInstitutionalIndication] = useManageIndication_UpdateIndicationMutation();

  const handleUpdateInstitutionalIndication = React.useCallback(
    async (values: IndicationFormValues) => {
      try {
        const { data } = await updateInstitutionalIndication({
          variables: {
            offeringId,
            indicationId: indication!.id,
            input: {
              instrumentId: values.instrumentId,
              currencyCode: values.currencyCode,
              interestLevels: values.interestLevels.map<InterestLevelInput>(
                ({
                  interestQuantity,
                  interestUnit,
                  limitPrice,
                  limitType,
                  coupon,
                  premium,
                  percentageOfPar,
                }) => ({
                  interestUnit: interestUnit!,
                  interestQuantity: interestQuantity!,
                  limitType,
                  limitPrice,
                  coupon,
                  premium,
                  percentageOfPar,
                })
              ),
            },
          },
          awaitRefetchQueries: true,
          refetchQueries,
        });
        SnackbarManager.success('Successfully updated Indication');
        pendingOrderBookGridForcedRefreshVar(true);

        const updatedIndication = data?.updateInstitutionalIndication!;
        updateDemandGridRowsReactiveVar({ fetchIds: [updatedIndication.id] });

        onFinishEditing();
      } catch {
        SnackbarManager.error('Failed to update Indication');
      }
    },
    [indication, offeringId, onFinishEditing, updateInstitutionalIndication]
  );

  return [handleUpdateInstitutionalIndication];
};
